import React, { useState, useRef, useEffect } from 'react';
import './Awards.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import apiService from '../services/api';

// --- Reusable Components ---

function Reveal({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'reveal-visible' : ''} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// --- Main Awards Component ---

export default function Awards() {
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch awards from API
  useEffect(() => {
    const fetchAwards = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await apiService.fetchAwards();
        console.log('🏆 Awards response:', result);
        
        if (result?.status === 'success' && result?.data?.length > 0) {
          // Filter only active awards (status might be 1 or 'active')
          const activeAwards = result.data.filter(a => {
            // Handle both numeric and string status
            if (a.status !== undefined) {
              return a.status === 1 || a.status === '1' || a.status === 'active';
            }
            return true;
          });
          
          // Sort by display_order
          const sortedAwards = activeAwards.sort((a, b) => 
            (parseInt(a.display_order) || 0) - (parseInt(b.display_order) || 0)
          );
          setAwards(sortedAwards);
          console.log(`✅ Loaded ${sortedAwards.length} active awards`);
        } else {
          console.warn('⚠️ No awards found or invalid response');
          setAwards([]);
        }
      } catch (err) {
        console.error('❌ Error fetching awards:', err);
        setError(err.message || 'Failed to load awards');
        setAwards([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAwards();
  }, []);

  // Helper function to parse image from JSON
  const getImageUrl = (imageData) => {
    if (!imageData) return null;
    try {
      const parsed = typeof imageData === 'string' ? JSON.parse(imageData) : imageData;
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed[0];
      }
      return parsed;
    } catch (e) {
      return imageData;
    }
  };

  // Clean HTML from Summernote
  const cleanHtml = (html) => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Format date - extract year
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.getFullYear();
    } catch (e) {
      return dateString;
    }
  };

  // Get icon based on title or description
  const getAwardIcon = (award) => {
    const title = (award.title_en || '').toLowerCase();
    const desc = (award.description_en || '').toLowerCase();
    const combined = title + ' ' + desc;
    
    if (combined.includes('padma') || combined.includes('shri')) return 'fa-award';
    if (combined.includes('dr. b.c. roy') || combined.includes('medical') || combined.includes('doctor')) return 'fa-user-md';
    if (combined.includes('president') || combined.includes('rashtrapati')) return 'fa-flag';
    if (combined.includes('prime minister') || combined.includes('modi') || combined.includes('pm')) return 'fa-flag';
    if (combined.includes('vice president') || combined.includes('vp')) return 'fa-flag';
    if (combined.includes('braille') || combined.includes('book') || combined.includes('biography')) return 'fa-book';
    if (combined.includes('science') || combined.includes('scientist')) return 'fa-flask';
    if (combined.includes('national')) return 'fa-trophy';
    if (combined.includes('best institution') || combined.includes('school')) return 'fa-university';
    if (combined.includes('children') || combined.includes('child')) return 'fa-child';
    if (combined.includes('gold')) return 'fa-medal';
    if (combined.includes('honour') || combined.includes('honor')) return 'fa-star';
    if (combined.includes('distinguished')) return 'fa-crown';
    return 'fa-medal';
  };

  // Get color based on year or title
  const getAwardColor = (award) => {
    const title = (award.title_en || '').toLowerCase();
    const year = formatDate(award.award_date);
    
    if (title.includes('president') || title.includes('prime minister')) return '#ff8c00';
    if (title.includes('national') || title.includes('best')) return '#2563eb';
    if (title.includes('padma') || title.includes('shri')) return '#dc2626';
    if (title.includes('science')) return '#7c3aed';
    if (title.includes('braille') || title.includes('book')) return '#0891b2';
    if (parseInt(year) > 2010) return '#2563eb';
    if (parseInt(year) > 2000) return '#7c3aed';
    return '#64748b';
  };

  return (
    <>
      <Header />
      <div className="awards-page devnar">
        <a className="skip-link" href="#main-awards">Skip to main content</a>

        {/* --- Page Hero --- */}
        <section className="page-hero-awards">
          <div className="hero-background">
            <div className="hero-overlay"></div>
            <div className="hero-pattern"></div>
          </div>
          <div className="wrap hero-content-awards">
            <Reveal>
              <span className="hero-badge">National Honours</span>
              <h1>Awards & Recognition</h1>
              <p className="hero-subtitle">
                Three decades of service — honoured by four Presidents, the Vice President, 
                and the Prime Minister of India.
              </p>
              <div className="hero-stats-mini">
                <div className="mini-stat">
                  <span className="stat-number">{awards.length}</span>
                  <span className="stat-label">Awards</span>
                </div>
                <div className="mini-stat">
                  <span className="stat-number">36</span>
                  <span className="stat-label">Years</span>
                </div>
                <div className="mini-stat">
                  <span className="stat-number">4</span>
                  <span className="stat-label">Presidents</span>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <main id="main-awards" className="wrap page-content-awards">

          {/* --- Loading State --- */}
          {loading && (
            <div className="loading-container">
              <div className="loader-spinner"></div>
              <p>Loading awards...</p>
            </div>
          )}

          {/* --- Error State --- */}
          {error && !loading && (
            <div className="error-container">
              <i className="fas fa-exclamation-circle"></i>
              <p>{error}</p>
              <button 
                className="btn btn-secondary" 
                onClick={() => window.location.reload()}
              >
                <i className="fas fa-redo"></i> Try Again
              </button>
            </div>
          )}

          {/* --- No Awards --- */}
          {!loading && !error && awards.length === 0 && (
            <div className="no-data-container">
              <i className="fas fa-trophy"></i>
              <p>No awards available at the moment. Please check back later.</p>
            </div>
          )}

          {/* --- Featured Awards Carousel --- */}
          {!loading && !error && awards.length > 0 && (
            <section className="featured-awards">
              <Reveal className="section-title" as="div">
                <span className="eyebrow">National Recognition</span>
                <h2>Honoured by the Nation</h2>
                <div className="rule" />
              </Reveal>
              
              <div className="awards-grid">
                {awards.map((award, index) => {
                  const imageUrl = getImageUrl(award.image);
                  const icon = getAwardIcon(award);
                  const color = getAwardColor(award);
                  const year = formatDate(award.award_date);
                  const description = cleanHtml(award.description_en);
                  
                  return (
                    <Reveal key={award.id || index} className="award-card" delay={index * 80}>
                      <div className="award-image-wrapper">
                        {imageUrl ? (
                          <img 
                            src={imageUrl} 
                            alt={award.title_en || 'Award'} 
                            loading="lazy"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                              e.target.parentElement.querySelector('.award-image-placeholder').style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className="award-image-placeholder" style={{ display: imageUrl ? 'none' : 'flex' }}>
                          <i className={`fas ${icon}`}></i>
                        </div>
                        {year && (
                          <span className="award-year" style={{ background: color }}>
                            {year}
                          </span>
                        )}
                        <span className="award-icon" style={{ background: color }}>
                          <i className={`fas ${icon}`}></i>
                        </span>
                      </div>
                      <div className="award-details">
                        <h3>{award.title_en || 'Award'}</h3>
                        {description ? (
                          <p>{description}</p>
                        ) : (
                          <p className="award-description-fallback">
                            {award.title_en || 'Recognition for outstanding contribution'}
                          </p>
                        )}
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            </section>
          )}

          {/* --- CTA Section --- */}
          <Reveal as="section" className="cta-band-awards">
            <div className="cta-content">
              <span className="cta-eyebrow">Support Our Mission</span>
              <h2>Help us continue this legacy of excellence.</h2>
              <p>Your support enables us to provide quality education, care, and opportunities to visually challenged children.</p>
              <div className="cta-buttons">
                <a className="btn btn-primary" href="/donate">
                  <i className="fas fa-heart"></i> Donate Now
                </a>
                <a className="btn btn-outline btn-outline-light" href="/volunteer">
                  <i className="fas fa-hands-helping"></i> Become a Volunteer
                </a>
              </div>
            </div>
          </Reveal>

        </main>
      </div>
      <Footer />
    </>
  );
}